<template>
  <div
    class="travelers-details-form"
    role="tablist"
  >
    <b-card
      v-for="(traveler, index) in traveler_details"
      :key="`traveler-details:${traveler.id}:${index}`"
      class="travelers-details-form__card"
      no-body
    >
      <b-card-header
        header-tag="header"
        class="p-0 border-0 rounded-0 bg-transparent"
        role="tab"
      >
        <button
          class="btn btn-ghost w-100 d-flex justify-content-between py-2 px-3"
          @click="() => toggle_expanded(traveler.id)"
        >
          <traveler-label
            :traveler_info="traveler"
            :index="index"
            with_icon
            show_age
          >
            <template>
              <vb-icon
                name="check_circle"
                :color="
                  has_filled_required_info(traveler.id)
                    ? color_success
                    : '#adb5bd'
                "
                :icon_style="{
                  '--color': has_filled_required_info(traveler.id)
                    ? color_success
                    : '#adb5bd',
                }"
                :size="14"
              />
            </template>
          </traveler-label>
          <chevron
            icon="chevron-down"
            :size="14"
            :is_open="is_expanded(traveler.id)"
          />
        </button>
      </b-card-header>
      <b-collapse :id="traveler.id" role="tabpanel" :visible="is_expanded(traveler.id)">
        <b-card-body class="pt-1">
          <b-row v-if="index === 0" class="travelers-details-form__note">
            <b-col sm="12">
              <div>
                Questo è il viaggiatore <span class="semibold">principale e Beneficiario</span> della prestazione welfare.
                <span class="semibold">A questi riferimenti invieremo</span>
                tutte le informazioni sulla prenotazione e sul volo.
              </div>
              <div>
                Inserisci email e numero di cellulare che consulti spesso.
              </div>
            </b-col>
          </b-row>
          <b-row v-if="is_infant(traveler)" class="travelers-details-form__note">
            <b-col sm="12">
              <div>
                Attenzione! I bambini da 0 a 2 anni viaggiano in braccio a un
                adulto e non hanno un posto a sedere.
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="2">
              <div class="form-group">
                <label>Titolo</label>
                <vb-select
                  v-model="traveler_details[index].gender"
                  :vb_options="{
                    id: build_input_id(traveler, 'gender'),
                    persistent_key: build_input_id(traveler, 'gender'),
                    options: genders,
                    text_field: 'text',
                    value_field: 'value',
                  }"
                />
              </div>
            </b-col>
            <b-col lg="3">
              <validation-provider
                v-slot="{ errors }"
                rules="required|alpha_spaces|min:2"
                :vid="'traveler_name_' + index"
                name="Nome"
              >
                <div class="form-group">
                  <label>Nome</label>
                  <vb-input
                    v-model="traveler_details[index].firstname"
                    :vb_options="{
                      id: build_input_id(traveler, 'firstname'),
                      persistent_key: build_input_id(traveler, 'firstname'),
                      autocomplete: 'chrome-off',
                    }"
                    @input="
                      (v) => {
                        if (index === 0) {
                          contact_details.firstname = v;
                        }
                      }
                    "
                  />
                </div>
                <span class="vee--errors">{{ errors[0] }}</span>
              </validation-provider>
            </b-col>
            <b-col lg="3">
              <validation-provider
                v-slot="{ errors }"
                rules="required|alpha_spaces|min:2"
                :vid="'traveler_surname_' + index"
                name="Cognome"
              >
                <div class="form-group">
                  <label>Cognome</label>
                  <vb-input
                    v-model="traveler_details[index].lastname"
                    :vb_options="{
                      id: build_input_id(traveler, 'lastname'),
                      persistent_key: build_input_id(traveler, 'lastname'),
                      autocomplete: 'chrome-off',
                    }"
                    @input="
                      (v) => {
                        if (index === 0) {
                          contact_details.lastname = v;
                        }
                      }
                    "
                  />
                </div>
                <span class="vee--errors">{{ errors[0] }}</span>
              </validation-provider>
            </b-col>
            <b-col lg="4">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                :vid="'traveler_birth_' + index"
                name="Data di nascita"
              >
                <div class="form-group">
                  <label
                    v-b-popover.hover.top="
                      !is_adult(traveler) ? 'La data di nascita deve corrispondere all\'età del passeggero indicata quando hai fatto la ricerca. L\'età da considerare è quella alla data di partenza per i voli di sola andata, o alla data dell\'ultimo volo se il viaggio include più tratte.' : ''
                    "
                    class="form-label"
                  >
                    Data di nascita
                    <vb-icon
                      v-if="!is_adult(traveler)"
                      name="info"
                      :size="12"
                      color="#aaa"
                    />
                  </label>
                  <div class="vb-pax__birth">
                    <birthdate-input
                      v-model="traveler_details[index].birth"
                      :min="get_date_range(traveler, index).min"
                      :max="get_date_range(traveler, index).max"
                      :input_id="build_input_id(traveler, 'birth')"
                      :persistent_key="build_input_id(traveler, 'birth')"
                      :corrected="!!birthdate_correction(traveler.id)"
                      @corrected="on_birth_corrected(traveler, index, $event)"
                    />
                  </div>
                </div>
                <span class="vee--errors">{{ errors[0] }}</span>
              </validation-provider>
            </b-col>
          </b-row>
          <b-row v-if="index === 0">
            <b-col lg="2">
              <div class="form-group">
                <label>Prefisso</label>
                <vb-select
                  v-model="contact_details.country_calling_code"
                  :vb_options="{
                    id: build_input_id(traveler, 'phone_code'),
                    persistent_key: build_input_id(traveler, 'phone_code'),
                    options: countries,
                    text_field: 'phone_display',
                    value_field: 'phone_code',
                  }"
                />
              </div>
            </b-col>
            <b-col lg="5">
              <div class="form-group">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required|min:6|numeric"
                  name="Numero di telefono"
                >
                  <label
                    v-b-popover.hover.top="
                      'Per ricevere importanti comunicazioni sulla tua prenotazione, inserisci un numero di cellulare che potrai consultare facilmente anche in mobilità durante il viaggio.'
                    "
                  >
                    Numero di telefono
                    <vb-icon
                      class="info-icon"
                      name="info"
                      :size="12"
                      color="#aaa"
                    />
                  </label>
                  <vb-input
                    v-model="contact_details.phone"
                    :vb_options="{
                      id: build_input_id(traveler, 'phone'),
                      persistent_key: build_input_id(traveler, 'phone'),
                      autocomplete: 'chrome-off',
                    }"
                  />
                  <span class="vee--errors">{{ errors[0] }}</span>
                </validation-provider>
              </div>
            </b-col>
            <b-col lg="5">
              <validation-provider
                v-slot="{ errors }"
                rules="required|email"
                name="Email"
              >
                <div class="form-group">
                  <label
                    v-b-popover.hover.top="
                      'Per ricevere importanti comunicazioni sulla tua prenotazione, inserisci un indirizzo che potrai consultare facilmente anche in mobilità durante il viaggio.'
                    "
                  >
                    Email
                    <vb-icon
                      class="info-icon"
                      name="info"
                      :size="12"
                      color="#aaa"
                    />
                  </label>
                  <vb-input
                    v-model="contact_details.email"
                    :vb_options="{
                      id: build_input_id(traveler, 'email'),
                      persistent_key: build_input_id(traveler, 'email'),
                      autocomplete: 'chrome-off',
                    }"
                  />
                  <span class="vee--errors">{{ errors[0] }}</span>
                </div>
              </validation-provider>
            </b-col>
          </b-row>
          <b-row v-if="index === 0 && is_address_required(traveler.id)">
            <b-col md="6" lg="4">
              <validation-provider
                v-slot="{ errors }"
                rules="required|max:35"
                name="Indirizzo"
              >
                <div class="form-group">
                  <label>Indirizzo</label>
                  <vb-input
                    v-model="contact_details.address"
                    :vb_options="{
                      id: build_input_id(traveler, 'address'),
                      persistent_key: build_input_id(traveler, 'address'),
                      autocomplete: 'chrome-off',
                    }"
                  />
                </div>
                <span class="vee--errors">{{ errors[0] }}</span>
              </validation-provider>
            </b-col>
            <b-col md="6" lg="4">
              <validation-provider
                v-slot="{ errors }"
                rules="required|alpha_spaces|max:35"
                name="Città"
              >
                <div class="form-group">
                  <label>Città</label>
                  <vb-input
                    v-model="contact_details.city"
                    :vb_options="{
                      id: build_input_id(traveler, 'city'),
                      persistent_key: build_input_id(traveler, 'city'),
                      autocomplete: 'chrome-off',
                    }"
                  />
                </div>
                <span class="vee--errors">{{ errors[0] }}</span>
              </validation-provider>
            </b-col>
            <b-col xs="6" sm="6" md="6" lg="2">
              <validation-provider
                v-slot="{ errors }"
                rules="required|min:3|max:6|alpha_num"
                name="CAP"
              >
                <div class="form-group">
                  <label>CAP</label>
                  <vb-input
                    v-model="contact_details.zipcode"
                    :vb_options="{
                      id: build_input_id(traveler, 'zipcode'),
                      persistent_key: build_input_id(traveler, 'zipcode'),
                    }"
                  />
                </div>
                <span class="vee--errors">{{ errors[0] }}</span>
              </validation-provider>
            </b-col>
            <b-col xs="6" sm="6" md="6" lg="2">
              <div class="form-group">
                <label>Paese</label>
                <vb-select
                  v-model="contact_details.country_code"
                  :vb_options="{
                    id: build_input_id(traveler, 'country_code'),
                    persistent_key: build_input_id(traveler, 'country_code'),
                    options: countries,
                    text_field: 'country_name',
                    value_field: 'country_code',
                  }"
                />
              </div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-collapse>
    </b-card>
  </div>
</template>

<script>
/**
 * Vincoli date di nascita (relativi a departure_date):
 *
 * HELD_INFANT / SEATED_INFANT: min = partenza - 2 anni + 1g, max = partenza - 2g
 * CHILD:                       min = partenza - 12 anni - 1g, max = partenza - 1g
 * ADULT:                       min = 1900-01-01,              max = partenza - 12 anni
 */
import BirthDateInput from "@components/blocks/birthdate-input.vue";
import TravelerLabel from "@liveflight/components/blocks/traveler/traveler-label.vue";

import { countries, genders } from "@/resources";
import {
  committed_age,
  enforced_age,
  get_traveler_birth_range_dates,
  store_key_age,
} from "@liveflight/utilities/travelers";
import { get_config, build_store_key, reverse_date } from "@/utilities";
import { birthdate_correction_mixin } from "@/utilities/birthdate-correction";
import { track_form_event } from "@/utilities/integrations/rybbit";
import {
  slim_flight_offer,
  slim_traveler,
} from "@liveflight/utilities/rybbit";
import { is_infant, is_adult } from "@liveflight/utilities";
import { color_success } from "@liveflight/resources/colors";

const TRAVELER_REQUIRED_FIELDS = ["gender", "firstname", "lastname", "birth"];

const CONTACT_REQUIRED_FIELDS = [
  ...TRAVELER_REQUIRED_FIELDS,
  "country_calling_code",
  "phone",
  "email",
];

const CONTACT_ADDRESS_FIELDS = ["address", "city", "zipcode", "country_code"];

export default {
  name: "travelers-details-form",
  components: {
    "traveler-label": TravelerLabel,
    "birthdate-input": BirthDateInput,
  },
  props: {
    contact_details: Object,
    flight_offer: Object,
    traveler_details: Array,
    booking_requirements: {
      type: Object,
      default: () => ({}),
    },
    committed_traveler_ages: {
      type: Object,
      default: () => ({}),
    },
  },
  mixins: [birthdate_correction_mixin],
  data() {
    return {
      color_success,
      genders,
      countries,
      tabState: "",
    };
  },
  computed: {
    openTabs: {
      get() {
        try {
          const parsed = JSON.parse(this.tabState);
          return parsed;
        } catch (e) {
          this.log("openTabs error");
          this.log(e);
          return {};
        }
      },
      set(value) {
        this.tabState = JSON.stringify(value);
      },
    },
    grouped_ids() {
      const counters = {};
      const grouped_ids = {};
      for (let index = 0; index < this.traveler_details.length; index += 1) {
        const traveler = this.traveler_details[index];
        counters[traveler.traveler_type] =
          (counters[traveler.traveler_type] || 0) + 1;
        grouped_ids[traveler.id] = counters[traveler.traveler_type];
      }
      return grouped_ids;
    },
    // L'età dichiarata va verificata alla data dell'ultimo volo, la stessa
    // ancora usata da get_traveler_birth_range_dates.
    last_flight_date() {
      const { lastDeparture = {} } = this.flight_offer || {};
      return reverse_date(lastDeparture.at);
    },
  },
  created() {
    if (this.traveler_details.length > 0) {
      const first_traveler_id = String(this.traveler_details[0].id);
      const tabState = {
        [first_traveler_id]: true,
      }
      if (
        this.traveler_details.length > 1 && 
        get_config('guiSettings.customizations.multiplePpenTravelerDetailsAllowed', false)
      ) {
        for (let traveler_index = 1; traveler_index < this.traveler_details.length; traveler_index += 1) {
          const { id: traveler_id } = this.traveler_details[traveler_index];
          tabState[traveler_id] = true;
        }
      }
      
      this.tabState = JSON.stringify(tabState);
      // Ensure first passenger is tied to contact details
      // Reactivity patch: $set, not index assignment, or pax 0 fields go non-reactive
      this.$set(this.traveler_details, 0, {
        ...this.traveler_details[0],
        firstname: this.contact_details.firstname || "",
        lastname: this.contact_details.lastname || "",
      });
    }
  },
  methods: {
    is_infant,
    is_adult,
    build_store_key,
    build_input_id(traveler, field) {
      const traveler_id = traveler.id;
      return this.build_store_key(
        this.grouped_ids[traveler_id] || traveler_id,
        traveler.traveler_type,
        store_key_age(traveler, this.committed_traveler_ages, traveler_id),
        field,
      );
    },
    is_expanded(traveler_id) {
      return !!this.openTabs[String(traveler_id)];
    },
    toggle_expanded(traveler_id) {
      const tab_key = String(traveler_id)
      const old = this.openTabs[tab_key];
      const openTabs = { ...this.openTabs }
      if (!get_config('guiSettings.customizations.multiplePpenTravelerDetailsAllowed', false)) {
        Object.keys(openTabs).forEach(tab => {
          openTabs[String(tab)] = false;
        });
      }
      openTabs[tab_key] = !old; 
      this.openTabs = openTabs;
    },
    get_date_range(traveler, traveler_index) {
      return get_traveler_birth_range_dates(
        this.flight_offer,
        traveler,
        traveler_index,
        committed_age(this.committed_traveler_ages, traveler.id)
      );
    },
    declared_age(traveler) {
      return enforced_age(
        traveler,
        committed_age(this.committed_traveler_ages, traveler.id)
      );
    },
    on_birth_corrected(traveler, traveler_index, correction) {
      // Anche una correzione che l'utente non ha causato (bound spostati, data
      // ripescata dallo storage) va detta: è il caso in cui se ne accorge meno.
      this.notify_birthdate_correction(
        traveler.id,
        correction,
        this.declared_age(traveler)
      );
      if (!correction) {
        return;
      }
      // Prima l'avviso, poi l'analytics: rybbit arriva da uno script esterno e
      // un suo errore, non intercettato, si mangerebbe il toast.
      try {
        this.track_birth_out_of_bounds(traveler, traveler_index, correction);
      } catch (e) {
        this.log(e);
      }
    },
    // Serve a ricostruire le prenotazioni partite con una data non voluta:
    // l'età attesa da sola non dice quale data l'utente avesse digitato.
    track_birth_out_of_bounds(traveler, traveler_index, correction) {
      const { min = "", max = "" } = this.get_date_range(
        traveler,
        traveler_index
      );
      track_form_event({
        event: "birthdate:validation:out-of-bounds",
        data: {
          type: "error",
          reason: "age_mismatch",
          expected_age: this.declared_age(traveler),
          entered_date: correction.from,
          corrected_date: correction.to,
          allowed_min: min,
          allowed_max: max,
          last_flight_date: this.last_flight_date,
          // 0 = l'ha causata lo spostamento dei bound, non una digitazione
          user_edited: correction.user_edited ? 1 : 0,
        },
        context: {
          traveler: slim_traveler(traveler),
          flightOffer: slim_flight_offer(this.flight_offer),
        },
      });
    },
    has_filled_required_info(traveler_id) {
      const index = this.traveler_details.findIndex(
        (traveler) => String(traveler_id) === String(traveler.id),
      );
      if (index < 0) {
        return false;
      }

      const required = [];
      if (index === 0) {
        required.push(...CONTACT_REQUIRED_FIELDS);
        if (this.is_address_required(traveler_id)) {
          required.push(...CONTACT_ADDRESS_FIELDS);
        }
      } else {
        required.push(...TRAVELER_REQUIRED_FIELDS);
      }

      const details =
        index === 0
          ? { ...this.traveler_details[index], ...this.contact_details }
          : this.traveler_details[index];
      const valued = Object.entries(details).filter(
        (entry) =>
          required.includes(entry[0]) &&
          !!entry[1] &&
          String(entry[1]).length > 0,
      );

      return valued.length === required.length;
    },
    is_address_required(traveler_id) {
      const traveler_requirements =
        this.booking_requirements.travelerRequirements || [];

      const index = traveler_requirements.findIndex(
        (requirement) => requirement.id === traveler_id,
      );
      if (index >= 0) {
        const requirements = traveler_requirements[index];
        return (
          requirements.redressRequiredIfAny !== false &&
          requirements.residenceRequired !== false
        );
      }

      // There have been errors even if not specified so default to required,
      // Hide only if explicit in the requirements
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.travelers-details-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-top: -($grid-gutter-width / 3);

  &__note {
    font-size: 14px;
    margin-bottom: $grid-gutter-width;
  }

  .form-group label {
    display: flex;
    gap: 5px;
    align-items: center;
    margin-left: $grid-gutter-width / 3;
    font-size: $font-size-md;
  }

  &__card {
    width: 100%;
    border-top: 0px;
    border-left: 0px;
    border-right: 0px;
    border-radius: 0;
    
    padding: $grid-gutter-width 0;
  }

}

</style>
